import { renderChildren, useResponsiveValue } from '@superdispatch/ui';
import { SidebarContainer } from '@superdispatch/ui-lab';
import { ReactNode, useEffect } from 'react';
import { Navigate, Outlet, RouteObject, useLocation } from 'react-router-dom';
import { setReportingKitTheme } from '@superdispatch/reporting-kit';
import { useAppThemeMode } from 'shared/theme/AppThemeProvider';
import { LoadsContextProvider } from '../loads/data/LoadsContext';
import { LoadsPageContextProvider } from '../loads/loads-page/data/LoadsPageParamsContext';
import { ReportsSidebar } from './core/ReportsSidebar';
import { ReportsContextProvider } from './data/ReportsContext';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { lazyImport } from 'shared/routing/LazyRoute';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { ReportsOverviewContextProvider } from './data/ReportsOverviewContext';
import { ReportsRevenueContextProvider } from './data/ReportsRevenueContext';
import { ReportsInvoicesContextProvider } from './data/ReportsInvoicesContext';
import { ReportsExpensesContextProvider } from './data/ReportsExpensesContext';
import { ReportsDeliveryTrendContextProvider } from './data/ReportsDeliveryTrendContext';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { resolveReportsDashboard } from './data/ReportsAnalytics';

const ReportsAccountsPayablePage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsAccountsPayablePage',
);
const ReportsAccountsReceivablePage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsAccountsReceivablePage',
);
const ReportsCompanyRevenuePage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsCompanyRevenuePage',
);
const ReportsCustomPage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsCustomPage',
);
const ReportsDriverPayPage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsDriverPayPage',
);
const ReportsLoadsPage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsLoadsPage',
);
const ReportsOverviewPage = lazyImport(
  () => import('./ReportsPages'),
  'ReportsOverviewPage',
);
const ReportsRevenuePage = lazyImport(
  () => import('./reports-revenue/ReportsRevenuePage'),
  'ReportsRevenuePage',
);
const ReportsExpensesPage = lazyImport(
  () => import('./reports-expenses/ReportsExpensesPage'),
  'ReportsExpensesPage',
);

const ReportsInvoicesPage = lazyImport(
  () => import('./reports-invoices/ReportsInvoicesPage'),
  'ReportsInvoicesPage',
);

const ReportsDeliveryTrendPage = lazyImport(
  () => import('./reports-delivery-trend/ReportsDeliveryTrendPage'),
  'ReportsDeliveryTrendPage',
);

function ReportsPage() {
  const isMobile = useResponsiveValue(true, false);
  const isReportingEnabled = useFlag('ctms_reporting');
  if (isMobile) {
    return <SidebarContainer sidebar={<ReportsSidebar />} />;
  }

  if (isReportingEnabled) {
    return <Navigate to="/reports/overview" />;
  }

  return <Navigate to="/reports/accounts-receivable" />;
}

function ReportsOutlet() {
  const isMobile = useResponsiveValue(true, false);

  if (isMobile) {
    return <Outlet />;
  }

  return (
    <SidebarContainer sidebar={<ReportsSidebar />}>
      <Outlet />
    </SidebarContainer>
  );
}

function ReportsContainerOutlet() {
  return (
    <PermissionView name="reports" permission="can_view">
      <LoadsContextProvider>
        <LoadsPageContextProvider>
          <ReportsContextProvider>
            <Outlet />
          </ReportsContextProvider>
        </LoadsPageContextProvider>
      </LoadsContextProvider>
    </PermissionView>
  );
}

function ReportingRoute({
  children,
  restricted = true,
}: {
  children: ReactNode;
  restricted?: boolean;
}) {
  const isReportingEnabled = useFlag('ctms_reporting');
  const theme = useAppThemeMode();
  const { pathname } = useLocation();
  const { isOpenSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();
  const { data: settings, isLoading: isSettingsLoading } = useCarrierSettings();

  useEffect(() => {
    setReportingKitTheme(theme);
  }, [theme]);

  if (!isReportingEnabled) {
    return <Navigate to="/reports/accounts-receivable" replace={true} />;
  }

  if (restricted && isSettingsLoading) return null;

  const shouldShowUpgradePlanDialog = !settings?.can_open_reporting;

  if (
    restricted &&
    shouldShowUpgradePlanDialog &&
    !isOpenSubscriptionCancelledDialog
  ) {
    return (
      <Navigate
        to="/reports/overview"
        replace={true}
        state={{
          showUpgradePlanDialog: true,
          restrictedDashboard: resolveReportsDashboard(pathname),
        }}
      />
    );
  }

  return renderChildren(children);
}

export const reportsRoutes: RouteObject[] = [
  {
    path: 'reports/:page?',
    element: <ReportsContainerOutlet />,
    children: [
      {
        path: 'loads',
        element: <ReportsLoadsPage />,
      },
      {
        path: '*',
        element: <ReportsOutlet />,
        children: [
          {
            path: 'overview',
            element: (
              <ReportingRoute restricted={false}>
                <ReportsOverviewContextProvider>
                  <ReportsOverviewPage />
                </ReportsOverviewContextProvider>
              </ReportingRoute>
            ),
          },
          {
            path: 'revenue/*',
            element: (
              <ReportingRoute>
                <ReportsRevenueContextProvider>
                  <ReportsRevenuePage />
                </ReportsRevenueContextProvider>
              </ReportingRoute>
            ),
          },
          {
            path: 'invoices/*',
            element: (
              <ReportingRoute>
                <ReportsInvoicesContextProvider>
                  <ReportsInvoicesPage />
                </ReportsInvoicesContextProvider>
              </ReportingRoute>
            ),
          },
          {
            path: 'expenses/*',
            element: (
              <ReportingRoute>
                <ReportsExpensesContextProvider>
                  <ReportsExpensesPage />
                </ReportsExpensesContextProvider>
              </ReportingRoute>
            ),
          },
          {
            path: 'delivery-trend/*',
            element: (
              <ReportingRoute>
                <ReportsDeliveryTrendContextProvider>
                  <ReportsDeliveryTrendPage />
                </ReportsDeliveryTrendContextProvider>
              </ReportingRoute>
            ),
          },
          {
            path: 'driver-pay',
            element: <ReportsDriverPayPage />,
          },
          {
            path: 'accounts-receivable',
            element: <ReportsAccountsReceivablePage />,
          },
          {
            path: 'company-revenue',
            element: <ReportsCompanyRevenuePage />,
          },
          {
            path: 'accounts-payable',
            element: <ReportsAccountsPayablePage />,
          },
          {
            path: 'custom-report/:reportGUID?/:action?',
            element: <ReportsCustomPage />,
          },
        ],
      },

      { index: true, element: <ReportsPage /> },
    ],
  },
];
